import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

// ON-CALL SERVICE REQUESTS (migration 102). One card for both places a
// request is entered: the customer portal's "Request service" card and the
// "Service requests" section of the owner's customer card. The server decides
// everything (who may ask, the window, duplicates); this renders its answer.
//
// The load answers { mode, minDate, maxDate, noteMax, upcoming, past }:
//   mode 'request'  the picker, the note, Submit and the lists
//   mode 'seasonal' the one-line note in `note` instead (portal only)
//   mode 'hidden'   nothing at all
// The submit answers the same lists plus `message` for dates already open.
//
// Dates are 'YYYY-MM-DD' calendar days in the company's zone from the server; they are
// labelled with UTC calendar arithmetic only, never the browser's zone.

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const parse = (ymd) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(ymd || ''));
  return m ? new Date(Date.UTC(+m[1], +m[2] - 1, +m[3])) : null;
};
const toYmd = (d) => `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;

// 'Tue Jan 14'.
export const formatRequestDay = (ymd) => {
  const d = parse(ymd);
  return d ? `${WEEKDAYS[d.getUTCDay()]} ${MONTHS[d.getUTCMonth()]} ${d.getUTCDate()}` : String(ymd || '');
};

// Every day from min through max, inclusive.
export const daysBetween = (minDate, maxDate) => {
  const out = [];
  const start = parse(minDate);
  const end = parse(maxDate);
  if (!start || !end) return out;
  for (let d = start; d <= end; d = new Date(d.getTime() + 86400000)) out.push(toYmd(d));
  return out;
};

const STATUS_CLASS = { fulfilled: 'sr-done', expired: 'sr-missed', cancelled: 'sr-cancelled' };

/**
 * props:
 *   loadUrl, createUrl       GET and POST
 *   cancelUrl(id)            POST
 *   headers                  extra request headers (the portal's token)
 *   title                    'Request service' or 'Service requests'
 *   intro                    a line under the title, optional
 *   submitLabel              'Request service' or 'Add date'
 *   variant                  'portal' | 'owner', for styling only
 *   onChange(data)           after any successful change, optional
 */
export default function ServiceRequestCard({
  loadUrl, createUrl, cancelUrl, headers, title, intro, submitLabel = 'Request service',
  variant = 'portal', onChange,
}) {
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState([]);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [cancellingId, setCancellingId] = useState(null);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const load = useCallback(async () => {
    try {
      const res = await axios.get(loadUrl, headers ? { headers } : undefined);
      setData(res.data || { mode: 'hidden' });
    } catch (err) {
      // A failed load hides the card rather than breaking the page around it.
      setData({ mode: 'hidden' });
    }
  }, [loadUrl, headers]);

  useEffect(() => { load(); }, [load]);

  if (!data || data.mode === 'hidden') return null;

  if (data.mode === 'seasonal') {
    return <p className="sr-seasonal-note" data-testid="sr-seasonal-note">{data.note}</p>;
  }

  const openDates = new Set((data.upcoming || []).map((r) => r.requestedDate));
  const days = daysBetween(data.minDate, data.maxDate);
  const noteMax = data.noteMax || 120;

  const toggle = (day) => {
    setMessage('');
    setSelected((prev) => (prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day].sort()));
  };

  const applyLists = (next) => {
    setData((prev) => ({ ...prev, upcoming: next.upcoming || [], past: next.past || [] }));
    if (onChange) onChange(next);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (selected.length === 0) { setError('Pick at least one date.'); return; }
    setSaving(true);
    setError('');
    setMessage('');
    try {
      const res = await axios.post(createUrl, { dates: selected, note: note.trim() || null }, headers ? { headers } : undefined);
      applyLists(res.data);
      const made = (res.data.created || []).map((c) => formatRequestDay(c.requestedDate));
      const madeText = made.length === 0 ? ''
        : variant === 'owner' ? `Added ${made.join(', ')}.` : `Got it. We have you down for ${made.join(', ')}.`;
      setMessage([
        madeText,
        res.data.message || '',
      ].filter(Boolean).join(' '));
      setSelected([]);
      setNote('');
    } catch (err) {
      setError(err.response?.data?.error || 'Could not save that request. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const cancel = async (item) => {
    setCancellingId(item.id);
    setError('');
    setMessage('');
    try {
      const res = await axios.post(cancelUrl(item.id), {}, headers ? { headers } : undefined);
      applyLists(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not cancel that request. Please try again.');
    } finally {
      setCancellingId(null);
    }
  };

  return (
    <section className={`sr-card sr-${variant}`} aria-labelledby={`sr-title-${variant}`}>
      <h3 id={`sr-title-${variant}`} className="sr-title">{title}</h3>
      {intro && <p className="sr-intro">{intro}</p>}

      <form onSubmit={submit}>
        <div className="sr-days" role="group" aria-label="Dates">
          {days.map((day, i) => {
            const taken = openDates.has(day);
            const on = selected.includes(day);
            return (
              <button
                key={day}
                type="button"
                className={`sr-day${on ? ' sr-day-on' : ''}${taken ? ' sr-day-taken' : ''}`}
                aria-pressed={on}
                disabled={taken || saving}
                title={taken ? 'Already requested' : undefined}
                onClick={() => toggle(day)}
              >
                {i === 0 ? `Today, ${formatRequestDay(day)}` : formatRequestDay(day)}
              </button>
            );
          })}
        </div>
        <label className="sr-note-label" htmlFor={`sr-note-${variant}`}>Note (optional)</label>
        <input
          id={`sr-note-${variant}`}
          className="sr-note"
          type="text"
          maxLength={noteMax}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Gate code, where to pile the snow..."
        />
        <div className="sr-note-count">{note.length}/{noteMax}</div>
        {error && <p className="sr-error" role="alert">{error}</p>}
        {message && <p className="sr-message" role="status">{message}</p>}
        <button type="submit" className="btn btn-primary sr-submit" disabled={saving || selected.length === 0}>
          {saving ? 'Saving...' : submitLabel}
        </button>
      </form>

      <div className="sr-lists">
        <h4 className="sr-subtitle">Upcoming</h4>
        {(data.upcoming || []).length === 0 ? (
          <p className="sr-empty">No dates requested.</p>
        ) : (
          <ul className="sr-list">
            {data.upcoming.map((item) => (
              <li key={item.id} className="sr-item">
                <span className="sr-date">{formatRequestDay(item.requestedDate)}</span>
                {item.note && <span className="sr-item-note">{item.note}</span>}
                <button
                  type="button"
                  className="btn btn-outline btn-sm sr-cancel"
                  onClick={() => cancel(item)}
                  disabled={cancellingId === item.id}
                  aria-label={`Cancel ${formatRequestDay(item.requestedDate)}`}
                >
                  {cancellingId === item.id ? 'Cancelling...' : 'Cancel'}
                </button>
              </li>
            ))}
          </ul>
        )}
        {(data.past || []).length > 0 && (
          <>
            <h4 className="sr-subtitle">Past</h4>
            <ul className="sr-list">
              {data.past.map((item) => (
                <li key={item.id} className="sr-item">
                  <span className="sr-date">{formatRequestDay(item.requestedDate)}</span>
                  <span className={`sr-status ${STATUS_CLASS[item.status] || ''}`}>{item.label}</span>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
      <style>{styles}</style>
    </section>
  );
}

const styles = `
  .sr-card { background: white; border-radius: 12px; padding: 20px; margin-bottom: 24px; }
  .sr-portal { box-shadow: 0 1px 3px rgba(0,0,0,0.1); }
  .sr-owner { border: 1px solid #e2e8f0; padding: 14px; margin: 12px 0; }
  .sr-title { font-size: 1.1rem; font-weight: 600; color: #0f172a; margin: 0 0 6px; }
  .sr-intro { color: #64748b; margin: 0 0 12px; font-size: 0.9rem; }
  .sr-days { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 12px; }
  .sr-day {
    border: 1px solid #cbd5e1; background: #f8fafc; color: #0f172a; border-radius: 8px;
    padding: 8px 10px; font-size: 0.85rem; cursor: pointer; min-height: 40px;
  }
  .sr-day-on { background: #1e88e5; border-color: #1e88e5; color: white; font-weight: 600; }
  .sr-day-taken { background: #e2e8f0; color: #94a3b8; cursor: not-allowed; text-decoration: line-through; }
  .sr-note-label { display: block; font-size: 0.85rem; color: #475569; margin-bottom: 4px; }
  .sr-note { width: 100%; box-sizing: border-box; padding: 8px 10px; border: 1px solid #cbd5e1; border-radius: 8px; font-size: 0.95rem; }
  .sr-note-count { text-align: right; font-size: 0.75rem; color: #94a3b8; margin: 2px 0 8px; }
  .sr-error { color: #b91c1c; font-weight: 600; margin: 0 0 8px; }
  .sr-message { color: #166534; background: #f0fdf4; border-radius: 6px; padding: 8px 10px; margin: 0 0 8px; }
  .sr-submit { min-height: 44px; }
  .sr-lists { margin-top: 16px; }
  .sr-subtitle { font-size: 0.9rem; color: #334155; margin: 12px 0 6px; }
  .sr-empty { color: #64748b; font-size: 0.9rem; margin: 0; }
  .sr-list { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 6px; }
  .sr-item { display: flex; align-items: center; gap: 10px; background: #f8fafc; border-radius: 8px; padding: 8px 10px; }
  .sr-date { font-weight: 600; color: #0f172a; min-width: 90px; }
  .sr-item-note { flex: 1; color: #475569; font-size: 0.85rem; overflow-wrap: anywhere; }
  .sr-cancel { margin-left: auto; }
  .sr-status { margin-left: auto; font-size: 0.8rem; font-weight: 600; padding: 2px 8px; border-radius: 10px; background: #e2e8f0; color: #334155; }
  .sr-done { background: #dcfce7; color: #166534; }
  .sr-missed { background: #fef3c7; color: #92400e; }
  .sr-cancelled { background: #f1f5f9; color: #64748b; }
  .sr-seasonal-note {
    background: white; border-radius: 12px; padding: 14px 20px; margin: 0 0 24px;
    color: #334155; box-shadow: 0 1px 3px rgba(0,0,0,0.1);
  }
`;
